import React, { useMemo, useState } from 'react';
import { Table, Tag, Tabs, Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import type { PicqerOrderFromApi } from '../../services/picqerOrdersService';
import type { WmsProvider } from '../../services/wmsOrdersService';

interface PicqerOrdersListProps {
  orders: PicqerOrderFromApi[];
  loading: boolean;
  wmsProvider?: WmsProvider;
  wmsName?: string;
}

function channeldockPublicOrderUrl(idorder: number): string {
  return `https://channeldock.com/portal/public/myorders/allorders?getOrder=${encodeURIComponent(String(idorder))}`;
}

type ChanneldockTabKey = 'open' | 'shipment' | 'return';

function cdStatusKey(o: PicqerOrderFromApi): string {
  return String(o.status ?? '')
    .toLowerCase()
    .trim();
}

function formatShortDateTime(value: string | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

export const PicqerOrdersList: React.FC<PicqerOrdersListProps> = ({
  orders,
  loading,
  wmsProvider = 'picqer',
  wmsName = 'Picqer',
}) => {
  const { t } = useTranslation();
  const [cdTab, setCdTab] = useState<ChanneldockTabKey>('open');

  const cdCounts = useMemo(() => {
    if (wmsProvider !== 'channeldock') return null;
    let open = 0;
    let shipment = 0;
    let ret = 0;
    for (const o of orders) {
      const s = cdStatusKey(o);
      if (s === 'shipment') shipment += 1;
      else if (s === 'return') ret += 1;
      else open += 1;
    }
    return { open, shipment, ret };
  }, [wmsProvider, orders]);

  const tableDataSource = useMemo(() => {
    if (wmsProvider !== 'channeldock') return orders;
    if (cdTab === 'shipment') return orders.filter((o) => cdStatusKey(o) === 'shipment');
    if (cdTab === 'return') return orders.filter((o) => cdStatusKey(o) === 'return');
    return orders.filter((o) => {
      const s = cdStatusKey(o);
      return s !== 'shipment' && s !== 'return';
    });
  }, [orders, wmsProvider, cdTab]);

  const columns: ColumnsType<PicqerOrderFromApi> = useMemo(() => {
    const linkColumn = {
      title: '',
      key: 'link',
      width: 44,
      render: (_: unknown, r: PicqerOrderFromApi) => {
        const href =
          wmsProvider === 'channeldock' && r.idorder != null && r.idorder > 0
            ? channeldockPublicOrderUrl(Number(r.idorder))
            : (r.public_status_page as string | undefined) ?? null;
        if (!href) return null;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={t('picqerOrders.openInProvider', { provider: wmsName })}
            aria-label={t('picqerOrders.openInProvider', { provider: wmsName })}
          >
            <LinkOutlined />
          </a>
        );
      },
    };

    const sharedBeforeDelivery: ColumnsType<PicqerOrderFromApi> = [
      {
        title: t('picqerOrders.orderId'),
        dataIndex: 'orderid',
        key: 'orderid',
        width: 120,
        render: (v: string, r: PicqerOrderFromApi) => v || r.orderid || '—',
      },
      {
        title: t('picqerOrders.providerId', { provider: wmsName }),
        dataIndex: 'idorder',
        key: 'idorder',
        width: 96,
        render: (v: number | string) => (v != null && v !== '' ? String(v) : '—'),
      },
      {
        title: t('picqerOrders.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
      },
      {
        title: t('picqerOrders.reference'),
        dataIndex: 'reference',
        key: 'reference',
        width: 110,
        ellipsis: true,
        render: (v: string, r: PicqerOrderFromApi) => {
          const cell = v ? <span title={v}>{v}</span> : '—';
          if (wmsProvider === 'channeldock' && r.order_note && String(r.order_note).trim()) {
            return (
              <Tooltip title={String(r.order_note)}>
                <span>{cell}</span>
              </Tooltip>
            );
          }
          return cell;
        },
      },
    ];

    const deliveryColumn: ColumnsType<PicqerOrderFromApi>[0] = {
      title: t('picqerOrders.delivery'),
      key: 'delivery',
      width: 200,
      ellipsis: true,
      render: (_: unknown, r: PicqerOrderFromApi) =>
        [r.deliveryname, r.deliverycity, r.deliverycountry].filter(Boolean).join(', ') || '—',
    };

    const createdColumn: ColumnsType<PicqerOrderFromApi>[0] = {
      title: t('picqerOrders.created'),
      dataIndex: 'created',
      key: 'created',
      width: 148,
      render: (createdAt: string) => formatShortDateTime(createdAt),
    };

    if (wmsProvider !== 'channeldock') {
      return [...sharedBeforeDelivery, deliveryColumn, createdColumn, linkColumn];
    }

    return [
      {
        title: t('picqerOrders.seqOrderId'),
        dataIndex: 'seq_order_id',
        key: 'seq_order_id',
        width: 120,
        ellipsis: true,
        render: (v: string | undefined) => (v ? <span title={v}>{v}</span> : '—'),
      },
      sharedBeforeDelivery[1],
      sharedBeforeDelivery[2],
      sharedBeforeDelivery[3],
      deliveryColumn,
      {
        title: t('picqerOrders.postcode'),
        dataIndex: 'deliveryzipcode',
        key: 'deliveryzipcode',
        width: 88,
        ellipsis: true,
        render: (v: string | undefined) => (v ? <span title={v}>{v}</span> : '—'),
      },
      {
        title: t('picqerOrders.street'),
        dataIndex: 'deliveryaddress',
        key: 'deliveryaddress',
        width: 140,
        ellipsis: true,
        render: (v: string | undefined) => (v ? <span title={v}>{v}</span> : '—'),
      },
      {
        title: t('picqerOrders.email'),
        dataIndex: 'emailaddress',
        key: 'emailaddress',
        width: 160,
        ellipsis: true,
        render: (v: string | undefined) => (v ? <span title={v}>{v}</span> : '—'),
      },
      {
        title: t('picqerOrders.updated'),
        dataIndex: 'updated',
        key: 'updated',
        width: 148,
        render: (v: string | undefined) => formatShortDateTime(v),
      },
      linkColumn,
    ];
  }, [t, wmsName, wmsProvider]);

  return (
    <div>
      {wmsProvider === 'channeldock' && cdCounts && (
        <Tabs
          activeKey={cdTab}
          onChange={(k) => setCdTab(k as ChanneldockTabKey)}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'open',
              label: `${t('picqerOrders.cdTabOpenOrders')} (${cdCounts.open})`,
            },
            {
              key: 'shipment',
              label: `${t('picqerOrders.cdTabShipment')} (${cdCounts.shipment})`,
            },
            {
              key: 'return',
              label: `${t('picqerOrders.cdTabReturn')} (${cdCounts.ret})`,
            },
          ]}
        />
      )}

      <Table
        rowKey="idorder"
        columns={columns}
        dataSource={tableDataSource}
        loading={loading}
        pagination={{ pageSize: 25 }}
        size="small"
        scroll={{ x: wmsProvider === 'channeldock' ? 1100 : undefined }}
      />
    </div>
  );
};
